import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const faqs = [
  {
    question: 'What is a Revenue OS?',
    answer: 'A Revenue Operating System sits on top of your existing GTM tools (CRM, call intelligence, email, Slack) and synthesizes all signals into one action per rep, per day. Instead of more dashboards, it tells your team exactly what to do next.'
  },
  {
    question: 'How is Forsyt different from Gong or Clari?',
    answer: 'Gong analyzes conversations. Clari forecasts pipeline. Forsyt prescribes action. It identifies the one stalled deal that matters most, pinpoints the real blocker, and tells your rep the exact next step, automatically.'
  },
  {
    question: 'Does Forsyt replace my CRM?',
    answer: 'No. Forsyt sits on top of your CRM (Salesforce, HubSpot, etc.) and enriches it with signals from calls, email, and Slack. Think of it as the action layer your CRM is missing.'
  },
  {
    question: 'How fast can I see results?',
    answer: 'Day 1. Connect your CRM and within hours, every rep has a prioritized action with context. No weeks of onboarding. Forsyt is designed for Week 1 ROI or your money back.'
  },
  {
    question: 'What data does Forsyt need access to?',
    answer: 'Forsyt integrates with your CRM (deal stages, contacts), call tools (Gong, Fireflies), email (Outlook, Gmail), and team messaging (Slack). All data stays encrypted and is never used for training.'
  },
  {
    question: 'Is there a free trial or demo?',
    answer: 'Yes. You can try our live sandbox demo right now to see Forsyt in action with sample data. For a personalized demo with your pipeline data, book a 30-minute call with our team.'
  },
  {
    question: 'How long does each stage take?',
    answer: 'Forsyt rolls in as Crawl, Walk, Run. Crawl is typically 1 to 2 weeks, Walk 3 to 6, and most teams are running automations by week 7. Day 1 is your time-to-first-action; crawl/walk/run is the depth ladder. Exit criteria are set with you before we start.'
  },
  {
    question: 'Can we stop after Crawl or Walk?',
    answer: 'Yes. Each stage stands on its own and ships something usable. Nothing requires you to buy the whole thing on day one.'
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section className="py-20 bg-gray-50" data-testid="faq-section">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
              '@type': 'Question',
              name: faq.question,
              acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
          })}
        </script>
      </Helmet>

      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="text-sm font-semibold text-blue-600 uppercase tracking-wider mb-2">FAQ</p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Common questions about Forsyt
          </h2>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-6 py-4 text-left"
              >
                <span className="text-sm font-semibold text-gray-900 pr-4">{faq.question}</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-48 pb-5' : 'max-h-0'
                }`}
              >
                <p className="px-6 text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
